/**
 * Component for rendering image content
 */

import { Spinner } from '@soteria/design-system-ve';
import { useState } from 'react';

import * as styles from './ImageContentRenderer.css';

interface ImageContentRendererProps {
  imageUrl: string;
  hasError: boolean;
  isLoading: boolean;
  onClear: () => void;
}

/**
 * Renders an image preview with a clear button
 * Shows a loading spinner when the image is being uploaded
 * Displays images directly - if image fails to load due to CORS or other issues, shows error message
 */
export function ImageContentRenderer({ imageUrl, hasError, isLoading, onClear }: ImageContentRendererProps) {
  const [imageLoadError, setImageLoadError] = useState(false);

  const handleImageError = () => {
    setImageLoadError(true);
  };

  const handleImageLoad = () => {
    setImageLoadError(false);
  };

  return (
    <div className={hasError ? styles.contentContainerError : styles.contentContainer}>
      <div className={styles.filePreview}>
        <div className={styles.contentTypeIndicator}>
          <span>Image</span>
        </div>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <Spinner label="Uploading image..." size="large" />
          </div>
        ) : imageLoadError ? (
          <div className={styles.errorMessageContainer}>
            <p>Unable to display image preview. Ensure URL is valid.</p>
            <p>CORS restrictions may prevent preview but classification may still work.</p>
            <p className={styles.imageUrlText}>{imageUrl}</p>
          </div>
        ) : (
          <img alt="Content preview" className={styles.imagePreview} src={imageUrl} onError={handleImageError} onLoad={handleImageLoad} />
        )}
        <div className={styles.buttonContainer}>
          <button className={styles.clearButton} onClick={onClear}>
            Clear
          </button>
        </div>
      </div>
    </div>
  );
}
